// Currency formatting
export const formatCurrency = (amount, currency = 'EUR', locale = 'fr-FR') => {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2
  }).format(amount || 0);
};

// Date formatting
export const formatDate = (date, locale = 'fr-FR') => {
  if (!date) return '';
  return new Intl.DateTimeFormat(locale, {
    day: '2-digit', month: '2-digit', year: 'numeric'
  }).format(new Date(date));
};

// Status colors
export const statusColors = {
  // Invoice status
  draft: 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300',
  sent: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
  paid: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300',
  partial: 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300',
  overdue: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300',
  cancelled: 'bg-slate-200 text-slate-500 dark:bg-slate-600 dark:text-slate-400',
  // Quote status
  accepted: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300',
  rejected: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300',
  expired: 'bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300',
  converted: 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300',
  // Delivery status
  pending: 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300',
  in_transit: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
  delivered: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300',
  returned: 'bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300',
  // Return status
  approved: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300',
  completed: 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300',
  // Letter of change status
  printed: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
};

// Currency symbols
export const currencies = [
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  { code: 'GBP', symbol: '£', name: 'British Pound' },
  { code: 'MAD', symbol: 'MAD', name: 'Dirham marocain' },
  { code: 'TND', symbol: 'TND', name: 'Dinar tunisien' },
  { code: 'DZD', symbol: 'DZD', name: 'Dinar algérien' },
  { code: 'XOF', symbol: 'XOF', name: 'Franc CFA' },
  { code: 'CAD', symbol: 'CA$', name: 'Canadian Dollar' },
  { code: 'CHF', symbol: 'CHF', name: 'Swiss Franc' },
  { code: 'JPY', symbol: '¥', name: 'Japanese Yen' },
  { code: 'CNY', symbol: '¥', name: 'Chinese Yuan' },
  { code: 'AED', symbol: 'AED', name: 'UAE Dirham' },
  { code: 'SAR', symbol: 'SAR', name: 'Saudi Riyal' }
];

// Supported languages
export const languages = [
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'ar', name: 'العربية', flag: '🇸🇦' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' }
];

// Calculate invoice totals from items (unitPrice = TTC)
export const calculateTotals = (items, discountType = 'percent', discountValue = 0) => {
  let subtotal = 0;
  let taxAmount = 0;

  const computedItems = items.map(item => {
    const lineTTC = (item.quantity || 0) * (item.unitPrice || 0);
    const discountAmt = item.discountType === 'percent'
      ? lineTTC * (item.discountValue || 0) / 100
      : (item.discountValue || 0);
    const afterDiscTTC = lineTTC - discountAmt;
    const taxFactor = 1 + (item.taxRate || 0) / 100;
    const itemTax = afterDiscTTC - afterDiscTTC / taxFactor;
    subtotal += afterDiscTTC;
    taxAmount += itemTax;
    return { ...item, discountAmount: discountAmt, taxAmount: itemTax, total: afterDiscTTC };
  });

  const discountAmount = discountType === 'percent'
    ? subtotal * (discountValue || 0) / 100
    : (discountValue || 0);
  const total = subtotal - discountAmount;

  return { items: computedItems, subtotal, discountAmount, taxAmount, total };
};

// Amount in words
export { numberToWords } from './numberToWords';

// Paginate invoice/delivery items for PDF printing
export function paginateItems(items) {
  if (!items || items.length === 0) return [[]];
  if (items.length <= 15) return [items];
  const pages = [];
  let remaining = [...items];
  pages.push(remaining.splice(0, 18));
  while (remaining.length > 0) pages.push(remaining.splice(0, 25));
  return pages;
}